import { useMemo } from "react";
import type { FC, ReactElement } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import { SkeletonLoading } from "@tenant/ui";
import clsx from "clsx";

import styles from "./styles.module.scss";

export type FieldProps = {
  label: string;
  errorText?: string | false;
  helperText?: string;
  onChangeText?: (text: string) => void;
  onChangeNumber?: (number: number) => void;
  onClickContainer?: () => void;
  isPopulated?: boolean;
  isFocused?: boolean;
  className?: string;
  containerClassName?: string;
  required?: boolean;
  children?: ReactElement;
  skeleton?: boolean;
  disabled?: boolean;
  hasInlineLabel?: boolean;
};

export const Field: FC<FieldProps> = ({
  label,
  errorText,
  helperText,
  onClickContainer,
  className,
  containerClassName,
  isPopulated,
  isFocused,
  required,
  children,
  skeleton,
  disabled,
  hasInlineLabel = false,
}) => {
  const hasError = useMemo(() => !!errorText, [errorText]);

  const [block, cn] = useModuleBEM(styles, "Field", {
    populated: isPopulated,
    focused: isFocused,
    error: hasError,
    disabled,
    skeleton,
  });

  return (
    <div className={clsx(block, className)} onClick={onClickContainer}>
      {!hasInlineLabel && label && (
        <label className={cn("label")}>
          {label}
          {required && <span className={cn("label-required")}>*</span>}
        </label>
      )}
      <div
        className={clsx(
          cn("container", { hasInlineLabel: hasInlineLabel }),
          containerClassName
        )}
      >
        {hasInlineLabel && (
          <label className={cn("inline-label")}>
            {label}
            {required && <span className={cn("label-required")}>*</span>}
          </label>
        )}
        {children}
        <SkeletonLoading className={cn("shimmer")} />
      </div>
      {errorText && <span className={cn("error-text")}>{errorText}</span>}
      {!errorText && helperText && (
        <span className={cn("helper-text")}>{helperText}</span>
      )}
    </div>
  );
};
